import { Injectable } from '@angular/core';
import { BehaviorSubject, Subscription, Observable, timer, first, Subject } from 'rxjs';
import { guid } from 'src/app/utils/Utils';

interface LoadingData {
  loaderId: string;
  timerSubscription: Subscription | null;
}

Injectable();
export class LoadingService {
  private _loadingThreshold = 300;
  private _loadingQueue: LoadingData[] = [];
  private _loadingBehaviorSubject: BehaviorSubject<boolean>;
  private _loaderIsDisplayed: Subject<boolean>;

  private _loaderImageUrl: BehaviorSubject<string> = new BehaviorSubject<string>('../../../assets/icons/logo.webp');
  public loaderImageUrl$: Observable<string> = this._loaderImageUrl.asObservable();

  public loading$: Observable<boolean>;
  public loadingDisplayed$: Observable<boolean>;

  constructor() {
    this._loadingBehaviorSubject = new BehaviorSubject<boolean>(false);
    this.loading$ = this._loadingBehaviorSubject.asObservable();
    this._loaderIsDisplayed = new Subject<boolean>();
    this.loadingDisplayed$ = this._loaderIsDisplayed.asObservable();
  }

  public setLoaderImage(url: string) {
    this._loaderImageUrl.next(url);
  }

  public startLoading() {
    const id = guid();
    this.setLoading(id);
    return id;
  }

  public clearLoadingQueue() {
    for (const loader of this._loadingQueue) {
      if (loader.timerSubscription) {
        loader.timerSubscription.unsubscribe();
      }
    }

    this._loadingQueue = [];
    this.updateLoadingState();
  }

  public getLoadingQueue() {
    return this._loadingQueue;
  }

  public isLoading() {
    return this._loadingBehaviorSubject.value;
  }

  public setLoading(loaderId: string) {
    if (!this._getQueueIds().includes(loaderId)) {
      let timerSubscription = null;

      if (this._loadingThreshold > 0) {
        timerSubscription = timer(this._loadingThreshold)
          .pipe(first())
          .subscribe(() => {
            this.updateLoadingState();
          });
      }

      this._loadingQueue.push({ loaderId, timerSubscription: timerSubscription });
    }
  }

  public stopLoading(loaderId: string) {
    const index = this._getLoaderIndex(loaderId);

    if (index !== -1) {
      const loader = this._loadingQueue[index];
      let updateLoadingState = true;

      if (loader.timerSubscription) {
        if (!loader.timerSubscription.closed) updateLoadingState = false;
        loader.timerSubscription.unsubscribe();
      }

      this._loadingQueue.splice(index, 1);
      if (updateLoadingState || this._loadingQueue.length === 0) this.updateLoadingState();
    }
  }

  public updateLoadingState() {
    const isLoading = this._loadingQueue.length > 0;
    this._loadingBehaviorSubject.next(isLoading);
  }

  public setLoaderVisible() {
    this._loaderIsDisplayed.next(true);
  }

  private _getQueueIds() {
    return this._loadingQueue.map((loader) => loader.loaderId);
  }

  private _getLoaderIndex(id: string) {
    return this._loadingQueue.findIndex((loader) => loader.loaderId === id);
  }
}
